/* ==========================================================================
   GR- Logo Marquee — Growthly
   assets/gr-logo-marquee.js

   The scroll itself is a CSS animation, so the strip moves with this file
   blocked. Everything here is refinement: measuring the loop distance,
   pausing off-screen, and drag-to-scrub.
   ========================================================================== */
(() => {
  'use strict';

  if (customElements.get('gr-logo-marquee')) return;

  const REDUCED = () => matchMedia('(prefers-reduced-motion: reduce)').matches;
  const DRAG_THRESHOLD = 6;

  class GRLogoMarquee extends HTMLElement {
    connectedCallback() {
      this.view = this.querySelector('[data-viewport]');
      this.track = this.querySelector('[data-track]');
      this.group = this.querySelector('[data-group-measure]');
      if (!this.view || !this.track || !this.group) return;

      this.track.dataset.direction = this.dataset.direction || 'left';
      this.track.style.setProperty('--gr-lm-speed', `${this.dataset.speed || 45}s`);

      if (this.dataset.pause === 'true') {
        this.querySelector('.gr-lm').classList.add('gr-lm--hover-pause');
      }

      this.measure();
      this.watchImages();
      this.observe();
      if (this.dataset.drag === 'true') this.bindDrag();

      /* Resize changes the group width, and the keyframe is written in terms
         of it. Debounced, because a window drag fires a burst of events and
         only the final size matters. */
      this._onResize = () => {
        clearTimeout(this._rt);
        this._rt = setTimeout(() => this.measure(), 150);
      };
      window.addEventListener('resize', this._onResize, { passive: true });
    }

    disconnectedCallback() {
      if (this._onResize) window.removeEventListener('resize', this._onResize);
      if (this._io) this._io.disconnect();
      if (this._ro) this._ro.disconnect();
      clearTimeout(this._rt);
      if (this._drag) {
        window.removeEventListener('pointermove', this._drag.move);
        window.removeEventListener('pointerup', this._drag.up);
        window.removeEventListener('pointercancel', this._drag.up);
      }
    }

    /* ---- measure -----------------------------------------------------------
       The keyframe translates by one group's width. That distance has to be a
       real pixel value, not a percentage: the track is `width: max-content`,
       so a percentage would resolve against the whole duplicated track and
       the loop would jump.
       ---------------------------------------------------------------------- */
    measure() {
      const w = this.group.getBoundingClientRect().width;
      if (!w) return;
      this.groupWidth = w;
      this.track.style.setProperty('--gr-lm-group-w', `${w}px`);
    }

    /* Logos are lazy-loaded, so the group is narrower than its final width on
       first measure. Re-measuring once they arrive keeps the loop seamless. */
    watchImages() {
      const imgs = [...this.group.querySelectorAll('img')];
      let left = imgs.filter((i) => !i.complete).length;
      if (!left) return;

      imgs.forEach((img) => {
        if (img.complete) return;
        const done = () => {
          left -= 1;
          if (left <= 0) this.measure();
        };
        img.addEventListener('load', done, { once: true });
        // A broken image must not leave the counter stuck forever.
        img.addEventListener('error', done, { once: true });
      });

      /* A ResizeObserver on the group catches anything the load events miss —
         a webfont swapping in and changing a wordmark's width, for instance. */
      if ('ResizeObserver' in window) {
        this._ro = new ResizeObserver(() => this.measure());
        this._ro.observe(this.group);
      }
    }

    /* ---- pause off-screen --------------------------------------------------
       An animation running below the fold costs battery and compositor time
       for something nobody can see.
       ---------------------------------------------------------------------- */
    observe() {
      if (!('IntersectionObserver' in window) || REDUCED()) return;
      this._io = new IntersectionObserver((entries) => {
        entries.forEach((en) => {
          this.track.classList.toggle('is-paused', !en.isIntersecting);
        });
      }, { threshold: 0 });
      this._io.observe(this);
    }

    /* ---- drag --------------------------------------------------------------
       Dragging pauses the animation and moves the track by hand, then hands
       control back on release.

       NO setPointerCapture: capturing retargets every later event, including
       the final click, to the element holding it — so a logo link would never
       open. Listening on window keeps the gesture alive off-element without
       touching event targeting.
       ---------------------------------------------------------------------- */
    bindDrag() {
      let startX = 0;
      let offset = 0;
      let base = 0;
      let armed = false;
      let dragging = false;
      let raf = 0;

      const paint = () => {
        raf = 0;
        // Wrap, so a long drag never runs the track off its own content.
        const w = this.groupWidth || 1;
        let x = base + offset;
        x = ((x % w) + w) % w - w;
        this.track.style.transform = `translate3d(${x}px, 0, 0)`;
      };

      const down = (e) => {
        if (e.pointerType === 'touch') return;  // native scroll handles touch
        if (e.button !== 0) return;
        armed = true;
        dragging = false;
        startX = e.clientX;

        /* Read where the animation currently is and freeze there, or the
           track snaps back to zero the moment it is paused. */
        const m = new DOMMatrixReadOnly(getComputedStyle(this.track).transform);
        base = m.m41 || 0;
        offset = 0;
      };

      const move = (e) => {
        if (!armed) return;
        const dx = e.clientX - startX;

        if (!dragging) {
          if (Math.abs(dx) <= DRAG_THRESHOLD) return;  // may still be a click
          dragging = true;
          this.track.classList.add('is-paused');
          this.view.classList.add('is-dragging');
          this._suppressClick = false;
        }
        if (e.cancelable) e.preventDefault();
        offset = dx;
        // Coalesce to one write per frame: pointermove fires far faster than
        // the screen repaints.
        if (!raf) raf = requestAnimationFrame(paint);
      };

      const up = () => {
        if (!armed) return;
        armed = false;
        if (raf) { cancelAnimationFrame(raf); raf = 0; }

        if (dragging) {
          this.view.classList.remove('is-dragging');
          /* Hand back to the animation. The inline transform must go, or it
             beats the keyframe and the strip stays frozen where it was left. */
          this.track.style.transform = '';
          this.track.classList.remove('is-paused');

          // Swallow the click this drag is about to produce, so a logo link
          // does not open when the visitor only meant to scrub.
          this._suppressClick = true;
          clearTimeout(this._st);
          this._st = setTimeout(() => { this._suppressClick = false; }, 400);
        }
        dragging = false;
      };

      this.view.addEventListener('click', (e) => {
        if (!this._suppressClick) return;
        this._suppressClick = false;
        e.preventDefault();
        e.stopPropagation();
      }, true);

      this.view.addEventListener('pointerdown', down);
      window.addEventListener('pointermove', move, { passive: false });
      window.addEventListener('pointerup', up);
      window.addEventListener('pointercancel', up);
      this.view.addEventListener('dragstart', (e) => e.preventDefault());

      this._drag = { move, up };
    }
  }

  customElements.define('gr-logo-marquee', GRLogoMarquee);
})();
